import React from "react";
import { Link } from "Components/Base/Navigation/Link";
import { LearningActivityCard } from "Components/Module/LearningActivityItem";
import { RatingChip } from "Components/Module/LearningActivityItem/Chips";
import { useFilterContext, useUIContext } from "../../context";
import { getReadMoreLink } from "../../utils";
import { getNextApplicationSortDisplayState } from "../../utils/nextApplicationSortDisplayUtils";
import CourseCardImage from "./CourseCardImage";
import CourseCardContent from "./CourseCardContent";
import CourseCardActions from "./CourseCardActions";

const CourseCard = ({
  learningActivity,
  languageContext,
  displayImage,
  listView,
  displayRating,
  isPublic = false,
  isNextApplicationSort = false,
  nextApplicationSortReferenceAt = null,
}) => {
  const {
    id,
    title,
    type,
    numberOfRatings,
    averageRating,
    learningActivityUrl,
    learningActivityExternalUrl,
  } = learningActivity;

  const { filters } = useFilterContext();
  const { uiState } = useUIContext();

  const readMoreLink = getReadMoreLink({
    learningActivityUrl,
    learningActivityExternalUrl,
    isPublic,
    filters,
    uiState,
  });

  const showRating =
    displayRating && Number(averageRating) > 0 && Number(numberOfRatings) > 0;

  const { nextApplicationSortKeyLabel, showNextApplicationSortDate } =
    getNextApplicationSortDisplayState({
      learningActivity,
      isNextApplicationSort,
      nextApplicationSortReferenceAt,
    });

  const courseCardImage = (
    <CourseCardImage
      imageUrl={learningActivity.imageUrl}
      title={title}
      displayImage={displayImage}
      decorativeImage={Boolean(readMoreLink)}
      sx={{ scale: "0.9" }}
    />
  );

  const imageComponent = readMoreLink ? (
    <Link
      href={readMoreLink}
      aria-label={`${languageContext("readmoreCourse")} ${title}`}
      title={`${languageContext("readmoreinfo")} ${title}`}
      underline="none"
      sx={{ display: "block", color: "inherit" }}
    >
      {courseCardImage}
    </Link>
  ) : (
    courseCardImage
  );

  return (
    <LearningActivityCard
      id={`learningActivityCard${id}`}
      key={`learningActivityCard-${id}`}
      title={title}
      titleLink={
        readMoreLink
          ? {
              href: readMoreLink,
              linkTitle: `${languageContext("readmoreinfo")} ${title}`,
            }
          : undefined
      }
      variant={listView ? "h3" : "h2"}
      type={type}
      displayImage={displayImage}
      displayIcon={false}
      imageComponent={imageComponent}
      chips={
        showRating ? (
          <RatingChip
            label={`${averageRating} (${numberOfRatings})`}
            ariaLabel={`${languageContext("coursegrade")}: ${averageRating} ${languageContext("outOfFive")}, ${numberOfRatings} ${languageContext("ratingsCount")}`}
          />
        ) : null
      }
      content={
        <CourseCardContent
          learningActivity={learningActivity}
          languageContext={languageContext}
          listView={listView}
        />
      }
      actions={
        <CourseCardActions
          readMoreLink={readMoreLink}
          title={title}
          languageContext={languageContext}
          showNextApplicationSortDate={showNextApplicationSortDate}
          nextApplicationSortKeyLabel={nextApplicationSortKeyLabel}
        />
      }
    />
  );
};

const MemoizedCourseCard = React.memo(CourseCard, (prevProps, nextProps) => {
  return (
    prevProps.learningActivity.id === nextProps.learningActivity.id &&
    prevProps.learningActivity.imageUrl ===
      nextProps.learningActivity.imageUrl &&
    prevProps.learningActivity.title === nextProps.learningActivity.title &&
    prevProps.learningActivity.learningActivityUrl ===
      nextProps.learningActivity.learningActivityUrl &&
    prevProps.learningActivity.learningActivityExternalUrl ===
      nextProps.learningActivity.learningActivityExternalUrl &&
    prevProps.learningActivity.averageRating ===
      nextProps.learningActivity.averageRating &&
    prevProps.learningActivity.numberOfRatings ===
      nextProps.learningActivity.numberOfRatings &&
    prevProps.displayImage === nextProps.displayImage &&
    prevProps.listView === nextProps.listView &&
    prevProps.displayRating === nextProps.displayRating &&
    prevProps.languageContext === nextProps.languageContext &&
    prevProps.isPublic === nextProps.isPublic &&
    prevProps.isNextApplicationSort === nextProps.isNextApplicationSort &&
    prevProps.nextApplicationSortReferenceAt ===
      nextProps.nextApplicationSortReferenceAt
  );
});

MemoizedCourseCard.displayName = "CourseCard";

export default MemoizedCourseCard;
